import React, { useCallback } from "react"
import PropTypes from "prop-types"
import Analytics from "lib/Analytics"
import FormFieldset from "components/FormFieldset"
import i18n from "lib/i18n"
import paymentMethodPropType from "components/PropTypes/paymentMethodPropType"

const PaymentMethodChooser = ({
  paymentMethod = "credit_card",
  active = true,
  creditCardAvailable = false,
  directDebitAvailable = false,
  payPalAvailable = false,
  onChange = () => {},
}) => {
  const PAYMENT_METHODS = {
    creditCard: {
      label: "donation.label.credit_card",
      value: "credit_card",
      available: creditCardAvailable,
    },
    directDebit: {
      label: "donation.label.direct_debit",
      value: "direct_debit",
      available: directDebitAvailable,
    },
    paypal: {
      label: "PayPal",
      value: "paypal",
      available: payPalAvailable,
    },
  }

  const handleChange = useCallback(
    (e) => {
      const paymentMethod = e.target.value
      Analytics.trackFormEvent("Donation Form", "Completed", "Payment Method", paymentMethod)
      onChange({ paymentMethod })
    },
    [onChange],
  )

  // If there is only one payment method for that region, no need to show the selector
  const numProviders = [creditCardAvailable, directDebitAvailable, payPalAvailable].filter(
    (p) => p,
  ).length

  if (numProviders < 2) {
    return null
  }

  return (
    <div className={"mx-auto mt-0 mb-5"} id="payment_method">
      <FormFieldset>
        <div className="text-xl font-semibold">{i18n.t("donation.heading.payment_method")}</div>
      </FormFieldset>

      {Object.values(PAYMENT_METHODS)
        .filter((method) => method.available)
        .map((method) => (
          <fieldset key={method.value}>
            <div className="relative mt-3 flex items-center gap-2">
              <label className="flex cursor-pointer items-center">
                <div className="group relative flex size-5 items-center justify-center align-middle">
                  <span className="pointer-events-none absolute inset-0 rounded-full transition-all group-hover:bg-gray-300/20 group-hover:ring-8 group-hover:ring-gray-300/20 group-active:animate-pulse group-active:bg-blue-600/40 group-active:ring-blue-600/40" />

                  <input
                    className="relative m-0 size-5 cursor-pointer rounded-full accent-blue-600"
                    aria-label={i18n.t(method.label)}
                    onChange={handleChange}
                    value={method.value}
                    type="radio"
                    name="payment_method"
                    checked={paymentMethod === method.value}
                    disabled={!active}
                  />
                </div>

                <span className="ml-3 text-sm">{i18n.t(method.label)}</span>
              </label>
            </div>
          </fieldset>
        ))}
    </div>
  )
}

PaymentMethodChooser.propTypes = {
  active: PropTypes.bool,
  onChange: PropTypes.func,
  creditCardAvailable: PropTypes.bool,
  directDebitAvailable: PropTypes.bool,
  payPalAvailable: PropTypes.bool,
  paymentMethod: paymentMethodPropType,
}

export default React.memo(PaymentMethodChooser)
